// Serializes user turns against the compaction turn that shares their driver.

import type { AgentEvent } from "@skaile/workspaces/types";

/** Opaque handle for one in-flight compaction. See {@link CompactionGate.begin}. */
export type CompactionHold = symbol;

/**
 * Coordinates the compaction-turn / user-turn race on a single driver.
 *
 * Compaction prompts the very driver a user turn would, and the runner
 * suppresses forwarded events for its duration. A turn dispatched into that
 * window therefore runs against a driver already mid-turn and has its reply
 * dropped on the way out — no text, no `finished`, no error, just an indefinite
 * "AI is thinking…" (skaile-ai/platform#3882).
 *
 * The window this gate holds is deliberately wider than the event suppression.
 * On the success path the orchestrator stops suppressing before it resets the
 * driver and injects the snapshot, so a turn released at that moment would race
 * the injection instead of the summary. The gate opens only once the whole
 * compaction call has settled.
 *
 * Mirrors {@link DriverSwapGate}: one instance per agent server, and every
 * method is safe to call when nothing is in flight.
 */
export class CompactionGate {
  /**
   * Every compaction currently holding the driver.
   *
   * A set rather than a boolean because holds can overlap: a driver swap
   * rebuilds the orchestrator, and the new instance carries its own
   * `_isCompacting`, so it can start a compaction while the previous
   * instance's call is still unwinding. Keyed by handle so the older one
   * settling cannot open the gate under the newer one — the same identity
   * check `DriverSwapGate.beginSwap` makes for swaps.
   */
  private held = new Set<CompactionHold>();
  /** Resolves when the last hold is released. Null whenever the driver is free. */
  private opened: Promise<void> | null = null;
  private release: (() => void) | null = null;

  /**
   * Mark a compaction as occupying the driver. The returned handle is the only
   * thing that can release it.
   */
  begin(): CompactionHold {
    const hold: CompactionHold = Symbol("compaction");
    if (this.held.size === 0) {
      this.opened = new Promise<void>((resolve) => {
        this.release = resolve;
      });
    }
    this.held.add(hold);
    return hold;
  }

  /**
   * Release one hold, opening the gate once none remain. Unknown and null
   * handles are ignored, so a settle signal that fires without a matching
   * `begin()` is harmless.
   *
   * Call this from a signal the orchestrator emits unconditionally. A `begin()`
   * whose `settle()` can be skipped holds user turns forever, which is a worse
   * failure than the discarded reply this gate exists to prevent.
   */
  settle(hold: CompactionHold | null | undefined): void {
    if (hold == null || !this.held.delete(hold)) return;
    if (this.held.size > 0) return;
    const release = this.release;
    this.opened = null;
    this.release = null;
    release?.();
  }

  /**
   * Wait for the driver to be free.
   *
   * Returns `idle` when nothing was in flight, `waited` when every compaction
   * settled within the bound, and `timeout` when they did not. A caller that
   * gets `timeout` must surface it rather than dispatch: a compaction still
   * holding the driver past this bound is stuck, and dispatching into it
   * reproduces the silent loss.
   *
   * Re-checks in a loop, as `DriverSwapGate.awaitSwap` does, so a compaction
   * armed while this turn was already waiting is also awaited. `timeoutMs`
   * bounds the whole wait, not each iteration.
   */
  async awaitIdle(timeoutMs: number): Promise<"idle" | "waited" | "timeout"> {
    if (this.held.size === 0) return "idle";

    const deadline = Date.now() + timeoutMs;
    let waited = false;
    while (this.held.size > 0) {
      const opened = this.opened;
      if (!opened) break;
      const remaining = deadline - Date.now();
      if (remaining <= 0) return "timeout";
      if ((await raceDeadline(opened, remaining)) === "timeout") return "timeout";
      waited = true;
    }
    return waited ? "waited" : "idle";
  }
}

async function raceDeadline(opened: Promise<void>, ms: number): Promise<"opened" | "timeout"> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<"timeout">((resolve) => {
    timer = setTimeout(() => resolve("timeout"), ms);
    timer.unref?.();
  });
  try {
    return await Promise.race([opened.then(() => "opened" as const), expired]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * 60 minutes, sized against the longest compaction that is still making
 * progress: a 10-minute bounded turn, one narrowed retry of the same, and then
 * an unbounded snapshot injection whose only ceiling is the driver's own
 * 30-minute turn-stall watchdog. That worst legitimate case is ~50 minutes, so
 * a tighter bound would report a working compaction as stuck.
 *
 * Bring it down once the injection turn is bounded (skaile-ai/workspaces#624);
 * until then this is the honest number.
 */
const DEFAULT_COMPACTION_GATE_TIMEOUT_MS = 60 * 60_000;

/**
 * How long a user turn waits for a compaction before the runner reports it as
 * stuck. Read per call so a test (or an operator) can set
 * `SKAILE_COMPACTION_GATE_TIMEOUT_MS` after this module is imported.
 */
export function compactionGateTimeoutMs(): number {
  const raw = Number(process.env.SKAILE_COMPACTION_GATE_TIMEOUT_MS);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_COMPACTION_GATE_TIMEOUT_MS;
}

/** The message a caller sends when it abandons a turn rather than race a compaction. */
export const COMPACTION_GATE_TIMEOUT_MESSAGE =
  "The session is still compacting its history and could not take this message. Send it again in a moment.";

export interface GuardTurnDeps {
  gate: CompactionGate;
  /** What is waiting, for the log line: "prompt", "reply". */
  what: string;
  log: (line: string) => void;
  sendEvent: (event: AgentEvent) => void;
  /** Defaults to {@link compactionGateTimeoutMs}. */
  timeoutMs?: number;
}

/**
 * Hold a user turn until the driver is free of any compaction.
 *
 * Returns false when the caller must abandon the turn — dispatching anyway is
 * what skaile-ai/platform#3882 records. The error event is the first
 * user-visible signal this failure has ever had: before it, the turn ran and
 * its reply was dropped in silence.
 */
export async function guardTurnAgainstCompaction(deps: GuardTurnDeps): Promise<boolean> {
  const outcome = await deps.gate.awaitIdle(deps.timeoutMs ?? compactionGateTimeoutMs());
  if (outcome === "waited") deps.log(`[serve] ${deps.what} released after compaction settled`);
  if (outcome !== "timeout") return true;
  deps.log(`[serve] ${deps.what} abandoned: a compaction has held the driver past its bound`);
  deps.sendEvent({ type: "error", message: COMPACTION_GATE_TIMEOUT_MESSAGE, fatal: false });
  return false;
}
